import { useEffect, useRef } from "react";

/* ──────────────────────────────────────────────────────────────────────────
   BEACH Analytics Dashboard — React port (1:1 of the original HTML file)

   Notes on the conversion:
   • All CSS is preserved verbatim and injected once via a <style> tag.
   • XLSX (SheetJS) and Chart.js are loaded from the same CDNs as the original.
   • The original IIFE logic (parser, chart builders, views, navigation) is
     preserved as-is and executed inside a useEffect after the libraries load.
     It manipulates the real DOM through ids/classes, exactly like the original,
     which is why the markup keeps every id/class intact.
   • `coca-cola-logo.png` is referenced relative to your app (same as original);
     drop that asset alongside the served page or swap the src for your path.
   ────────────────────────────────────────────────────────────────────────── */

const CSS = `
:root{
  --red:#E61A27;--red-deep:#B3121C;--red-light:#FDE8EA;--red-bg:#FFF5F5;
  --white:#FFFFFF;--off-white:#F8F9FA;--grey-50:#F5F5F5;--grey-100:#EEEEEE;
  --grey-200:#E0E0E0;--grey-300:#BDBDBD;--grey-400:#9E9E9E;--grey-500:#757575;
  --grey-600:#616161;--grey-700:#424242;--grey-800:#212121;--grey-900:#121212;
  --green:#2E7D32;--green-light:#E8F5E9;--amber:#F57F17;--amber-light:#FFF8E1;
  --blue:#1565C0;--shadow:0 2px 12px rgba(0,0,0,.08);
  --radius:12px;--gut:clamp(16px,3vw,32px);
}
*{box-sizing:border-box;margin:0;padding:0}
html,body{font-family:'Inter',system-ui,sans-serif;background:var(--off-white);color:var(--grey-800);
  -webkit-font-smoothing:antialiased;min-height:100vh}
::selection{background:var(--red);color:#fff}
::-webkit-scrollbar{width:6px;height:6px}
::-webkit-scrollbar-thumb{background:var(--grey-300);border-radius:6px}

#upload-screen{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;
  background:linear-gradient(180deg,#fff 0%,var(--off-white) 100%);padding:40px 20px}
.upload-logo{width:180px;height:auto;margin-bottom:8px;mix-blend-mode:multiply}
.upload-title{font-size:28px;font-weight:800;color:var(--grey-800);margin-bottom:4px;text-align:center}
.upload-sub{font-size:14px;color:var(--grey-500);margin-bottom:32px;text-align:center}
.drop-zone{width:min(520px,90vw);border:2px dashed var(--grey-300);border-radius:20px;padding:48px 32px;
  text-align:center;cursor:pointer;transition:.2s;background:#fff}
.drop-zone:hover,.drop-zone.dragover{border-color:var(--red);background:var(--red-bg)}
.drop-zone .icon{font-size:48px;margin-bottom:12px;color:var(--red)}
.drop-zone h3{font-size:18px;font-weight:700;color:var(--grey-700);margin-bottom:6px}
.drop-zone p{font-size:13px;color:var(--grey-400)}
.drop-zone input{display:none}
.upload-hint{margin-top:20px;font-size:12px;color:var(--grey-400);text-align:center;max-width:400px;line-height:1.6}
#upload-progress{display:none;margin-top:20px;text-align:center}
.progress-bar{width:240px;height:6px;background:var(--grey-100);border-radius:6px;overflow:hidden;margin:10px auto}
.progress-fill{height:100%;background:var(--red);border-radius:6px;width:0;transition:width .3s}
#upload-status{font-size:13px;color:var(--grey-500);margin-top:8px}

#dashboard{display:none;min-height:100vh}
.dash-header{display:flex;align-items:center;gap:16px;padding:12px var(--gut);
  background:#fff;border-bottom:1px solid var(--grey-200);position:sticky;top:0;z-index:50}
.dash-header .logo{height:28px;width:auto;mix-blend-mode:multiply}
.dash-header .divider{width:1px;height:28px;background:var(--grey-200)}
.dash-header .title{font-size:15px;font-weight:700;color:var(--grey-800)}
.dash-header .title span{color:var(--red)}
.dash-header .spacer{flex:1}
.dash-header .period-badge{display:flex;align-items:center;gap:6px;background:var(--red-light);
  border:1px solid rgba(230,26,39,.2);border-radius:20px;padding:5px 14px;font-size:12px;font-weight:600;color:var(--red)}
.dash-header .file-name{font-size:11px;color:var(--grey-400);max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.btn{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:8px;font-size:12px;
  font-weight:600;cursor:pointer;border:1px solid var(--grey-200);background:#fff;color:var(--grey-700);transition:.15s}
.btn:hover{border-color:var(--red);color:var(--red)}

.tab-bar{display:flex;gap:0;padding:0 var(--gut);background:#fff;border-bottom:1px solid var(--grey-200)}
.tab{padding:12px 20px;font-size:13px;font-weight:600;color:var(--grey-500);cursor:pointer;
  border-bottom:2px solid transparent;transition:.15s}
.tab:hover{color:var(--grey-800)}
.tab.active{color:var(--red);border-bottom-color:var(--red)}

.filter-bar{display:flex;flex-wrap:wrap;gap:10px;padding:12px var(--gut);background:#fff;
  border-bottom:1px solid var(--grey-100)}
.filter-group{display:flex;flex-direction:column;gap:3px}
.filter-group label{font-size:10px;font-weight:700;color:var(--grey-400);text-transform:uppercase;letter-spacing:.5px}
.filter-group select{padding:6px 28px 6px 10px;border:1px solid var(--grey-200);border-radius:8px;
  font-size:12px;font-family:inherit;color:var(--grey-700);background:#fff;cursor:pointer;
  appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%239E9E9E' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 8px center;min-width:140px}
.filter-group select:focus{outline:none;border-color:var(--red)}

.dash-content{padding:20px var(--gut)}

.kpi-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px;margin-bottom:20px}
.kpi-card{background:#fff;border:1px solid var(--grey-100);border-radius:var(--radius);padding:14px 16px;
  box-shadow:var(--shadow);position:relative;overflow:hidden;transition:.15s}
.kpi-card:hover{border-color:var(--red);transform:translateY(-2px);box-shadow:0 4px 20px rgba(0,0,0,.1)}
.kpi-card::before{content:'';position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--kpi-color,var(--red))}
.kpi-label{font-size:11px;font-weight:600;color:var(--grey-500);text-transform:uppercase;letter-spacing:.4px;margin-bottom:6px}
.kpi-change{display:inline-flex;align-items:center;gap:3px;font-size:9px;font-weight:700;padding:1px 5px;border-radius:20px;margin-left:4px}
.kpi-change.up{background:var(--green-light);color:var(--green)}
.kpi-change.down{background:var(--red-light);color:var(--red)}
.kpi-change.flat{background:var(--amber-light);color:var(--amber)}
.kpi-hi,.kpi-lo{display:flex;align-items:center;gap:6px;padding:4px 0;flex-wrap:wrap}
.kpi-hi{border-bottom:1px solid var(--grey-100)}
.kpi-hi strong,.kpi-lo strong{font-size:12px;color:var(--grey-800);flex:none}
.kpi-hi-val,.kpi-lo-val{font-size:16px;font-weight:900;color:var(--grey-800);margin-left:auto}
.kpi-hi-label{font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--green);
  background:var(--green-light);padding:1px 5px;border-radius:4px;flex:none}
.kpi-lo-label{font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--red);
  background:var(--red-light);padding:1px 5px;border-radius:4px;flex:none}

.charts-row{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px}
.charts-row.tri{grid-template-columns:1fr 1fr 1fr}
@media(max-width:1100px){.charts-row.tri{grid-template-columns:1fr 1fr}}
@media(max-width:900px){.charts-row,.charts-row.tri{grid-template-columns:1fr}}
.chart-card{background:#fff;border:1px solid var(--grey-100);border-radius:var(--radius);padding:18px;box-shadow:var(--shadow)}
.chart-card h3{font-size:14px;font-weight:800;color:var(--grey-800);margin-bottom:4px}
.chart-card .chart-sub{font-size:11px;color:var(--grey-400);margin-bottom:14px}
.chart-wrap{position:relative;width:100%;height:300px}
.chart-wrap canvas{width:100%!important;height:100%!important}

.section-divider{display:flex;align-items:center;gap:14px;margin:32px 0 20px;padding:0}
.section-divider .line{flex:1;height:1px;background:var(--grey-200)}
.section-divider .badge{background:var(--red);color:#fff;padding:6px 18px;border-radius:20px;
  font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;white-space:nowrap}
.filter-hint{text-align:center;padding:40px 20px;color:var(--grey-400);font-size:13px;
  border:2px dashed var(--grey-200);border-radius:var(--radius);margin-top:12px}

.table-section{background:#fff;border:1px solid var(--grey-100);border-radius:var(--radius);
  box-shadow:var(--shadow);overflow:hidden;margin-bottom:20px}
.table-header{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;
  border-bottom:1px solid var(--grey-100)}
.table-header h3{font-size:14px;font-weight:800}
.table-header .count{font-size:12px;color:var(--grey-400)}
.table-wrap{overflow-x:auto;max-height:440px;overflow-y:auto}
table{width:100%;border-collapse:collapse;font-size:12px}
thead{position:sticky;top:0;z-index:2}
thead th{background:var(--grey-50);color:var(--grey-600);text-align:left;padding:10px 12px;
  font-size:10.5px;text-transform:uppercase;letter-spacing:.4px;font-weight:700;border-bottom:2px solid var(--grey-200);
  white-space:nowrap}
tbody td{padding:9px 12px;border-bottom:1px solid var(--grey-100);white-space:nowrap}
tbody tr:hover{background:var(--red-bg)}
.brand-cell{display:flex;align-items:center;gap:8px;font-weight:600}
.brand-dot{width:8px;height:8px;border-radius:50%;flex:none}
.tccc{font-weight:700;color:var(--red)}
.val-up{color:var(--green);font-weight:700}
.val-down{color:var(--red);font-weight:700}

.sheet-nav{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:16px}
.sheet-btn{padding:6px 14px;border:1px solid var(--grey-200);border-radius:20px;font-size:11px;
  font-weight:600;cursor:pointer;background:#fff;color:var(--grey-600);transition:.15s}
.sheet-btn:hover{border-color:var(--red);color:var(--red)}
.sheet-btn.active{background:var(--red);color:#fff;border-color:var(--red)}

@media(max-width:640px){
  .kpi-row{grid-template-columns:repeat(2,1fr)}
  .filter-bar{overflow-x:auto;flex-wrap:nowrap}
}
`;

/* Load an external script once; resolve immediately if already present. */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src === src)) {
      resolve();
      return;
    }
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("Failed to load " + src));
    document.head.appendChild(el);
  });
}

/* The original dashboard logic, preserved verbatim (was an IIFE). Runs once,
   after XLSX + Chart.js have loaded and after the markup is mounted. */
function runApp() {
  "use strict";

  const BRAND_COLORS = {
    'Coca-Cola/ Coca-Cola Classic': '#E61A27', 'Coca-Cola': '#E61A27',
    'Sprite': '#0B8A2F', 'Thums Up': '#C6930A', 'Fanta': '#FF8C00', 'Limca': '#00ACC1',
    'Maaza': '#F9A825', '7-Up': '#7CB342', 'Pepsi': '#004B93', 'Mountain Dew': '#8BC34A',
    'Mirinda': '#E65100', 'Sting': '#7B1FA2', 'Frooti': '#D32F2F', 'Campa Cola': '#6D4C41',
    'Tropicana Slice': '#FF5722', 'Appy Fizz': '#3949AB', 'Charged by Thums Up': '#546E7A',
    'Diet Coke / Coca-Cola Light': '#B71C1C', 'Coca-Cola Zero/ No Sugar': '#212121',
    'Minute Maid': '#EF6C00', 'Lahori': '#00695C', 'Pepsi Black': '#263238',
    'Campa Lemon': '#AFB42B', 'Campa Orange': '#FF6F00', 'Campa': '#5D4037',
    'Rim-Zim': '#26A69A', 'Maaza Aam Panna': '#FFB300', 'Thums Up X-Force': '#FF6F00',
    'Sprite Light/ No Sugar/ Zero': '#43A047', 'Diet/Light Pepsi': '#1565C0',
    'Minute Maid Pulpy': '#F4511E', 'Tropicana': '#4CAF50', 'Slice': '#9C27B0'
  };
  const TCCC = new Set(['Coca-Cola/ Coca-Cola Classic', 'Coca-Cola', 'Sprite', 'Thums Up', 'Fanta', 'Limca',
    'Maaza', 'Maaza Aam Panna', 'Minute Maid', 'Minute Maid Pulpy', 'Sting', 'Appy Fizz',
    'Charged by Thums Up', 'Diet Coke / Coca-Cola Light', 'Coca-Cola Zero/ No Sugar',
    'Sprite Light/ No Sugar/ Zero', 'Rim-Zim', 'Thums Up X-Force']);
  const FILTER_LABELS = new Set(['Geography Hierarchy', 'L1_Region', 'Top_40_flag_name',
    'Age Range', 'Gender_Name', 'Generation_Bucket', 'RTD_NRTD', 'SEC Group', 'Period Hierarchy',
    'Country_ID', 'Period_Type', 'Commercial_Beverages', 'Is Active Imagery',
    'Manufacturer Group', 'Trademark', 'BACK', 'INDIA', 'Month_Name']);
  const BOUNDARY_LABELS = new Set([...FILTER_LABELS, 'L2_State', 'Category', 'Drink Type']);
  const VALID_CATS = new Set(['Sparkling Soft Drinks', 'Energy Drinks', 'SSD Regular',
    'SSD Regular + Energy Drinks', 'SSD Low Cal', 'RTD Fruit Juice',
    'Total Juices/Juice Drinks/Smoothies', 'Still/Spark. Bottle Water', 'Intenders']);
  const METRIC_MAP = {
    'Weekly+': 'Weekly+ Incidence', 'Past 7 Day': 'Past 7 Day Drinkers',
    'Spont Awareness': 'Spontaneous Awareness', 'TOM': 'TOM',
    'Total Awareness': 'Total Awareness', 'Affinity & Cons TB': 'Affinity TB',
    'WINR': 'WINR', 'AH_AFH': 'At Home / Away From Home',
    'Affinity & Cons T2B': 'Affinity T2B', 'Brand Power ': 'Brand Power',
    'Occasions Shares': 'Occasion Share', 'Imagery': 'Imagery',
    ' Occasions Abs': 'Occasions Abs', 'Occasions (Teens)': 'Occasions (Teens)',
    'Awareness (Teens)': 'Awareness (Teens)', 'Affinity & Consideration (Teen)': 'Affinity (Teen)',
    'Spont Awareness SSD+ED': 'Spontaneous Awareness SSD+ED', 'TOM SSD+ED': 'TOM SSD+ED',
    'Total Awareness SSD+ED': 'Total Awareness SSD+ED',
    'Affinity & Cons TB -SSD+ED': 'Affinity TB SSD+ED', 'Brand Power SSD+ED': 'Brand Power SSD+ED',
    'Reasons': 'Reasons', 'Affinity and Considration T2B': 'Affinity T2B (Alt)',
    'Imagery SSD+ED': 'Imagery SSD+ED', 'Equity Trended': 'Equity Trended',
    'Weekly+ Demo': 'Weekly+ Demo', 'P7D W+ Demo': 'P7D W+ Demo', 'P7D Demo': 'P7D Demo'
  };
  const ABS_SHEETS = new Set(['WINR', 'Brand Power ', 'Brand Power SSD+ED', 'Weekly+ Demo', 'P7D W+ Demo', 'P7D Demo', ' Occasions Abs', 'Occasions (Teens)']);
  function brandColor(b) { return BRAND_COLORS[b] || '#9E9E9E'; }
  const BRAND_DISPLAY = {
    'Coca-Cola/ Coca-Cola Classic': 'Coca-Cola', 'Coca-Cola Zero/ No Sugar': 'Coca-Cola Zero',
    'Diet Coke / Coca-Cola Light': 'Diet Coke', 'Sprite Light/ No Sugar/ Zero': 'Sprite Zero',
    'Diet/Light Pepsi': 'Diet Pepsi', 'Charged by Thums Up': 'Thums Up Charged'
  };
  function shortBrand(b) { return BRAND_DISPLAY[b] || b; }
  const PALETTE = ['#E61A27', '#1565C0', '#2E7D32', '#F57F17', '#7B1FA2', '#00695C', '#E65100', '#3949AB', '#6D4C41', '#00ACC1', '#8BC34A', '#FF5722', '#795548', '#9C27B0', '#546E7A'];
  function itemColor(item, dim) {
    if (dim === 'Brand') return brandColor(item);
    let h = 0; for (let i = 0; i < item.length; i++) h = ((h << 5) - h) + item.charCodeAt(i);
    return PALETTE[Math.abs(h) % PALETTE.length];
  }

  /* ═══ PARSER ═══ */
  let PD = { sheets: {}, brands: new Set(), categories: new Set(), periods: [], states: new Set() };

  function isPeriodCell(v) { return v.includes('12M/E') || /^MOD \d{4}$/.test(v); }

  function parseOneTable(rows, headerIdx) {
    const hdr = rows[headerIdx]; if (!hdr) return null;
    const periods = [], dimNames = [], chLYIdx = [], chLMIdx = [];
    let seenPeriod = false;
    for (let j = 0; j < hdr.length; j++) {
      const v = String(hdr[j] || '').trim(); if (!v) continue;
      if (isPeriodCell(v)) { if (seenPeriod && periods.some(p => p.name === v)) break; periods.push({ col: j, name: v }); seenPeriod = true; }
      else if (v === 'Ch vs LY') chLYIdx.push(j);
      else if (v === 'Ch vs LM') chLMIdx.push(j);
      else if (!FILTER_LABELS.has(v) && v !== 'Grand Total' && v !== '(blank)' && v !== 'Values' && v !== 'Month_Name' && !v.includes('Incremental') && !v.includes('>>')) {
        if (seenPeriod && dimNames.some(d => d.name === v)) break; dimNames.push({ col: j, name: v });
      }
    }
    if (periods.length < 2) return null;
    const hasBrand = dimNames.some(d => d.name === 'Brand');
    const hasState = dimNames.some(d => d.name === 'L2_State');
    const primaryDim = hasBrand ? 'Brand' : (dimNames.length > 0 ? dimNames[0].name : 'Brand');
    const data = []; let emptyRun = 0;
    for (let i = headerIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || !row.some(c => c !== null && c !== undefined && c !== '')) { emptyRun++; if (emptyRun >= 2) break; continue; }
      emptyRun = 0; const fc = String(row[0] || '').trim(); if (BOUNDARY_LABELS.has(fc)) break;
      const dims = {}; dimNames.forEach(d => { const v = row[d.col]; if (v != null) dims[d.name] = String(v); });
      if (!Object.values(dims).some(v => v && v !== '(blank)')) continue;
      const values = {}; let hasVal = false;
      periods.forEach(p => { const v = row[p.col]; if (v != null && !isNaN(v)) { values[p.name] = parseFloat(v); hasVal = true; } });
      if (!hasVal) continue;
      const entry = { dims, values, chVsLY: null, chVsLM: null };
      if (chLYIdx.length) { const v = parseFloat(row[chLYIdx[0]]); if (!isNaN(v)) entry.chVsLY = v; }
      if (chLMIdx.length) { const v = parseFloat(row[chLMIdx[0]]); if (!isNaN(v)) entry.chVsLM = v; }
      data.push(entry);
    }
    if (!data.length) return null;
    return { periods: periods.map(p => p.name), dimNames: dimNames.map(d => d.name), primaryDim, hasBrand, hasState, metric: '', data, endRow: headerIdx + 1 + data.length + emptyRun };
  }

  function parseWorkbook(wb) {
    const result = { sheets: {}, brands: new Set(), categories: new Set(), periods: [], states: new Set() };
    const allPeriodSet = new Set();
    wb.SheetNames.forEach(name => {
      if (name === 'START HERE' || name === 'Sheet1') return;
      try {
        const ws = wb.Sheets[name];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
        if (!rows || rows.length < 5) return;
        const headerRows = [];
        for (let i = 0; i < rows.length; i++) { const row = rows[i]; if (!row) continue; if (row.filter(c => c && isPeriodCell(String(c))).length >= 2) headerRows.push(i); }
        if (!headerRows.length) return;
        let indiaTable = null, stateTable = null, extraTables = [];
        for (const hIdx of headerRows) {
          if (stateTable && hIdx < stateTable.endRow) continue;
          if (indiaTable && !stateTable && hIdx < indiaTable.endRow) continue;
          const tbl = parseOneTable(rows, hIdx); if (!tbl) continue;
          tbl.periods.forEach(p => allPeriodSet.add(p));
          if (tbl.hasState) { if (!stateTable) stateTable = tbl; tbl.data.forEach(d => { if (d.dims.L2_State && d.dims.L2_State !== 'Grand Total') result.states.add(d.dims.L2_State); }); }
          else { if (!indiaTable) indiaTable = tbl; else extraTables.push(tbl); }
        }
        if (!indiaTable && !stateTable) return;
        const primary = indiaTable || stateTable;
        primary.data.forEach(d => {
          if (d.dims.Brand && d.dims.Brand !== 'Grand Total' && d.dims.Brand !== '(blank)') result.brands.add(d.dims.Brand);
          const cat = d.dims.Category || d.dims['Drink Type'] || '';
          if (cat && VALID_CATS.has(cat)) result.categories.add(cat);
        });
        if (stateTable) stateTable.data.forEach(d => { if (d.dims.Brand && d.dims.Brand !== 'Grand Total' && d.dims.Brand !== '(blank)') result.brands.add(d.dims.Brand); });
        const brandExtra = extraTables.find(t => t.hasBrand);
        result.sheets[name] = { metric: primary.metric || METRIC_MAP[name] || name, periods: primary.periods, dimNames: primary.dimNames, primaryDim: primary.primaryDim, hasBrand: primary.hasBrand, data: primary.data, stateData: stateTable ? stateTable.data : [], statePeriods: stateTable ? stateTable.periods : primary.periods, stateDims: stateTable ? stateTable.dimNames : primary.dimNames, brandOccData: brandExtra ? brandExtra.data : null };
      } catch (e) { console.warn('Parse fail:', name, e); }
    });
    const periodOrder = p => { const m = p.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})/); if (!m) { const m2 = p.match(/MOD (\d{4})/); return m2 ? parseInt(m2[1]) * 100 : 0; } const mo = { Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6, Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12 }; return parseInt(m[2]) * 100 + mo[m[1]]; };
    result.periods = [...allPeriodSet].sort((a, b) => periodOrder(a) - periodOrder(b));
    return result;
  }

  /* ═══ DATA ACCESS ═══ */
  const $ = s => document.querySelector(s);
  const $$ = s => [...document.querySelectorAll(s)];
  function curGeo() { return ($('#fGeo') || {}).value || 'All India'; }
  function curPeriod() { return PD.periods[PD.periods.length - 1] || ''; }

  function getData(sh) { const s = PD.sheets[sh]; if (!s) return []; const g = curGeo(); if (g === 'All India') return s.data; return s.stateData.filter(d => d.dims.L2_State === g); }
  function getPeriods(sh) { const s = PD.sheets[sh]; if (!s) return PD.periods; return curGeo() === 'All India' ? s.periods : s.statePeriods; }
  function getDim(sh) { const s = PD.sheets[sh]; if (!s) return 'Brand'; if (curGeo() !== 'All India' && s.stateDims && s.stateDims.length) { const sd = s.stateDims.filter(n => n !== 'L2_State'); return sd.find(n => n === 'Brand') || sd[0] || s.primaryDim; } return s.primaryDim; }
  function isPct(sh) { return !ABS_SHEETS.has(sh); }

  function findRow(sh, dimVal, dim) {
    const data = getData(sh); const d = dim || getDim(sh);
    if (dimVal === 'All') { const gt = data.find(r => (r.dims[d] || '') === 'Grand Total'); if (gt) return gt; const p = curPeriod(); return data.filter(r => r.dims[d] && r.dims[d] !== 'Grand Total' && r.dims[d] !== '(blank)' && r.values[p] !== undefined).sort((a, b) => (b.values[p] || 0) - (a.values[p] || 0))[0] || null; }
    return data.find(r => (r.dims[d] || '') === dimVal);
  }

  function topItems(sh, n, cat, brand) {
    const data = getData(sh); const s = PD.sheets[sh]; if (!s) return [];
    const dim = getDim(sh); const p = curPeriod();
    let items = data.filter(d => d.dims[dim] && d.dims[dim] !== 'Grand Total' && d.dims[dim] !== '(blank)' && d.values[p] !== undefined);
    if (cat && cat !== 'All' && s.hasBrand) items = items.filter(d => (d.dims.Category || d.dims['Drink Type'] || '') === cat);
    if (brand && brand !== 'All' && dim === 'Brand') items = items.filter(d => d.dims.Brand === brand || TCCC.has(d.dims.Brand));
    return items.sort((a, b) => (b.values[p] || 0) - (a.values[p] || 0)).slice(0, n).map(d => d.dims[dim]);
  }

  function hasFilter() {
    return curGeo() !== 'All India' || ($('#fMetric') || {}).value !== 'All' || ($('#fCategory') || {}).value !== 'All' || ($('#fBrand') || {}).value !== 'All';
  }

  /* ═══ FORMATTERS ═══ */
  function fP(v, d = 1) { return (v == null || isNaN(v)) ? '—' : (v * 100).toFixed(d) + '%'; }
  function fPP(v) { if (v == null || isNaN(v)) return '—'; const p = (v * 100).toFixed(2); return (v >= 0 ? '+' : '') + p + ' pts'; }
  function fN(v) { if (v == null || isNaN(v)) return '—'; if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(1) + 'M'; if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(1) + 'K'; return v.toFixed(1); }
  function fV(v, pct) { return pct ? fP(v) : fN(v); }
  function chDir(v) { return v > 0.0001 ? 'up' : v < -0.0001 ? 'down' : 'flat'; }
  function chArr(v) { return v > 0.0001 ? '&#9650;' : v < -0.0001 ? '&#9660;' : '&#9644;'; }
  function shortP(p) { return p.replace(' 12M/E', '').replace('MOD ', ''); }

  /* ═══ CHART BUILDERS ═══ */
  let charts = {};
  function killChart(id) { if (charts[id]) { charts[id].destroy(); delete charts[id]; } }

  function mkTrend(id, sh, items, periods) {
    killChart(id); const s = PD.sheets[sh]; if (!s) return; const pct = isPct(sh); const dim = getDim(sh); const data = getData(sh);
    const ds = items.map(b => { const row = data.find(d => (d.dims[dim] || '') === b); if (!row) return null; const c = itemColor(b, dim);
      return { label: dim === 'Brand' ? shortBrand(b) : b, data: periods.map(p => { const v = row.values[p]; return v !== undefined ? (pct ? v * 100 : v) : null; }), borderColor: c, backgroundColor: c + '20', borderWidth: 2, pointRadius: 2, pointHoverRadius: 5, tension: .3, fill: false }; }).filter(Boolean);
    const ctx = document.getElementById(id); if (!ctx || !ds.length) return;
    charts[id] = new Chart(ctx, { type: 'line', data: { labels: periods.map(shortP), datasets: ds }, options: { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8, font: { size: 10, family: 'Inter' } } } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 45 } }, y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => pct ? v.toFixed(1) + '%' : fN(v) } } } } });
  }

  function mkBar(id, sh, period, n) {
    killChart(id); const s = PD.sheets[sh]; if (!s) return; const pct = isPct(sh); const dim = getDim(sh); const data = getData(sh);
    let items = data.filter(d => d.dims[dim] && d.dims[dim] !== 'Grand Total' && d.dims[dim] !== '(blank)' && d.values[period] !== undefined).map(d => ({ n: d.dims[dim], v: d.values[period] })).sort((a, b) => (b.v || 0) - (a.v || 0)).slice(0, n || 15);
    const ctx = document.getElementById(id); if (!ctx || !items.length) return;
    charts[id] = new Chart(ctx, { type: 'bar', data: { labels: items.map(i => dim === 'Brand' ? shortBrand(i.n) : i.n), datasets: [{ data: items.map(i => pct ? i.v * 100 : i.v), backgroundColor: items.map(i => itemColor(i.n, dim)), borderRadius: 4, barPercentage: .7 }] }, options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => pct ? v.toFixed(1) + '%' : fN(v) } }, y: { grid: { display: false }, ticks: { font: { size: 10, family: 'Inter' } } } } } });
  }

  function mkChange(id, sh, period, items) {
    killChart(id); const s = PD.sheets[sh]; if (!s) return; const dim = getDim(sh); const data = getData(sh);
    const rows = items.map(b => { const r = data.find(d => (d.dims[dim] || '') === b); return r ? { n: b, ly: r.chVsLY, lm: r.chVsLM } : null; }).filter(i => i && (i.ly !== null || i.lm !== null));
    if (!rows.length) return; const ctx = document.getElementById(id); if (!ctx) return;
    charts[id] = new Chart(ctx, { type: 'bar', data: { labels: rows.map(i => dim === 'Brand' ? shortBrand(i.n) : i.n), datasets: [
      { label: 'Ch vs LY', data: rows.map(i => (i.ly || 0) * 100), backgroundColor: rows.map(i => i.ly >= 0 ? '#2E7D32' : '#E61A27'), borderRadius: 3, barPercentage: .4, categoryPercentage: .8 },
      { label: 'Ch vs LM', data: rows.map(i => (i.lm || 0) * 100), backgroundColor: rows.map(i => i.lm >= 0 ? '#81C784' : '#EF9A9A'), borderRadius: 3, barPercentage: .4, categoryPercentage: .8 }] },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10, family: 'Inter' } } } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 45 } }, y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(1) + ' pts' } } } } });
  }

  function mkDonut(id, labels, vals, colors) {
    killChart(id); const ctx = document.getElementById(id); if (!ctx) return;
    charts[id] = new Chart(ctx, { type: 'doughnut', data: { labels, datasets: [{ data: vals, backgroundColor: colors, borderWidth: 2, borderColor: '#fff' }] }, options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'right', labels: { boxWidth: 10, padding: 8, font: { size: 10, family: 'Inter' } } } } } });
  }

  function mkPie(id, labels, vals, colors) {
    killChart(id); const ctx = document.getElementById(id); if (!ctx) return;
    charts[id] = new Chart(ctx, { type: 'pie', data: { labels, datasets: [{ data: vals, backgroundColor: colors, borderWidth: 2, borderColor: '#fff' }] }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { boxWidth: 10, padding: 6, font: { size: 10, family: 'Inter' } } } } } });
  }

  function mkRadar(id, labels, datasets) {
    killChart(id); const ctx = document.getElementById(id); if (!ctx) return;
    charts[id] = new Chart(ctx, { type: 'radar', data: { labels, datasets }, options: { responsive: true, maintainAspectRatio: false, scales: { r: { beginAtZero: true, ticks: { font: { size: 9 }, backdropColor: 'transparent' }, pointLabels: { font: { size: 10, family: 'Inter' } }, grid: { color: '#e0e0e0' } } }, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10, family: 'Inter' } } } } } });
  }

  function mkScatter(id, datasets, xLabel, yLabel) {
    killChart(id); const ctx = document.getElementById(id); if (!ctx) return;
    charts[id] = new Chart(ctx, { type: 'scatter', data: { datasets }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10, family: 'Inter' } } }, tooltip: { callbacks: { label: item => { const d = item.raw; return d.label + ': (' + d.x.toFixed(1) + '%, ' + d.y.toFixed(1) + '%)'; } } } }, scales: { x: { title: { display: true, text: xLabel, font: { size: 11, family: 'Inter' } }, grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(0) + '%' } }, y: { title: { display: true, text: yLabel, font: { size: 11, family: 'Inter' } }, grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(0) + '%' } } } } });
  }

  function mkBubble(id, datasets, xLabel, yLabel) {
    killChart(id); const ctx = document.getElementById(id); if (!ctx) return;
    charts[id] = new Chart(ctx, { type: 'bubble', data: { datasets }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10, family: 'Inter' } } }, tooltip: { callbacks: { label: item => { const d = item.raw; return d.label + ': ' + xLabel + '=' + d.x.toFixed(1) + '%, ' + yLabel + '=' + d.y.toFixed(1) + '%'; } } } }, scales: { x: { title: { display: true, text: xLabel, font: { size: 11, family: 'Inter' } }, grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(0) + '%' } }, y: { title: { display: true, text: yLabel, font: { size: 11, family: 'Inter' } }, grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(0) + '%' } } } } });
  }

  /* ═══ SECTION 1: OVERALL PICTURE ═══ */
  function buildKPIs(period) {
    const kpis = [{ s: 'Weekly+', l: 'Weekly+ Incidence', c: '#E61A27' }, { s: 'Past 7 Day', l: 'Past 7 Day Drinkers', c: '#1565C0' }, { s: 'TOM', l: 'TOM', c: '#7B1FA2' }, { s: 'Spont Awareness', l: 'Spontaneous Awareness', c: '#00695C' }, { s: 'Total Awareness', l: 'Total Awareness', c: '#F57F17' }, { s: 'Affinity & Cons TB', l: 'Affinity TB', c: '#E65100' }];
    let h = '<div class="kpi-row">';
    kpis.forEach(k => {
      const sData = getData(k.s);
      const sorted = sData.filter(r => r.dims.Brand && r.dims.Brand !== 'Grand Total' && r.dims.Brand !== '(blank)' && r.values[period] !== undefined).sort((a, b) => (b.values[period] || 0) - (a.values[period] || 0));
      const hi = sorted[0], lo = sorted[sorted.length - 1];
      h += `<div class="kpi-card" style="--kpi-color:${k.c}"><div class="kpi-label">${k.l}</div>
        <div class="kpi-hi"><span class="kpi-hi-label">Highest</span> <strong>${hi ? shortBrand(hi.dims.Brand) : ''}</strong>
          <span class="kpi-hi-val">${hi ? fP(hi.values[period]) : '—'}</span>
          ${hi && hi.chVsLY != null ? `<span class="kpi-change ${chDir(hi.chVsLY)}">${chArr(hi.chVsLY)} ${fPP(hi.chVsLY)}</span>` : ''}</div>
        <div class="kpi-lo"><span class="kpi-lo-label">Lowest</span> <strong>${lo ? shortBrand(lo.dims.Brand) : ''}</strong>
          <span class="kpi-lo-val">${lo ? fP(lo.values[period]) : '—'}</span>
          ${lo && lo.chVsLY != null ? `<span class="kpi-change ${chDir(lo.chVsLY)}">${chArr(lo.chVsLY)} ${fPP(lo.chVsLY)}</span>` : ''}</div></div>`;
    });
    return h + '</div>';
  }

  /* ═══ SECTION 2: FILTERED DEEP-DIVE ═══ */
  function buildFilteredSection(ms, period, cat, brand, geo) {
    const dim = getDim(ms); const data = getData(ms); const pct = isPct(ms); const mL = METRIC_MAP[ms] || ms;
    const filteredBrands = data.filter(d => d.dims[dim] && d.dims[dim] !== 'Grand Total' && d.dims[dim] !== '(blank)' && d.values[period] !== undefined);

    let h = `<div class="section-divider"><span class="line"></span><span class="badge">Filtered Analysis — ${mL} — ${geo}</span><span class="line"></span></div>`;

    h += `<div class="charts-row tri">
      <div class="chart-card"><h3>Share Distribution</h3><div class="chart-sub">${mL}, ${shortP(period)}</div>
        <div class="chart-wrap"><canvas id="fPie"></canvas></div></div>
      <div class="chart-card"><h3>Brand Equity Radar</h3><div class="chart-sub">Top brands across key metrics</div>
        <div class="chart-wrap"><canvas id="fRadar"></canvas></div></div>
      <div class="chart-card"><h3>Filtered Trend</h3><div class="chart-sub">${mL}, rolling periods</div>
        <div class="chart-wrap"><canvas id="fLine"></canvas></div></div></div>`;

    h += `<div class="charts-row">
      <div class="chart-card"><h3>Brand Positioning</h3><div class="chart-sub">Weekly+ Incidence vs TOM</div>
        <div class="chart-wrap"><canvas id="fScatter"></canvas></div></div>
      <div class="chart-card"><h3>Awareness vs Affinity</h3><div class="chart-sub">Bubble size = Weekly+ Incidence</div>
        <div class="chart-wrap"><canvas id="fBubble"></canvas></div></div></div>`;

    return h;
  }

  function renderFilteredCharts(ms, period, cat, brand) {
    const dim = getDim(ms); const data = getData(ms); const pct = isPct(ms);

    // 1. Pie — top 12 items by share
    const pieItems = data.filter(d => d.dims[dim] && d.dims[dim] !== 'Grand Total' && d.dims[dim] !== '(blank)' && d.values[period] !== undefined)
      .map(d => ({ n: d.dims[dim], v: d.values[period] })).sort((a, b) => b.v - a.v).slice(0, 12);
    if (pieItems.length) mkPie('fPie', pieItems.map(i => dim === 'Brand' ? shortBrand(i.n) : i.n), pieItems.map(i => pct ? i.v * 100 : i.v), pieItems.map(i => itemColor(i.n, dim)));

    // 2. Radar — top 5 brands across key metrics
    const radarMetrics = [{ s: 'Weekly+', l: 'Weekly+' }, { s: 'Past 7 Day', l: 'P7D' }, { s: 'TOM', l: 'TOM' }, { s: 'Spont Awareness', l: 'Awareness' }, { s: 'Total Awareness', l: 'Tot. Aware.' }, { s: 'Affinity & Cons TB', l: 'Affinity' }];
    const radarLabels = radarMetrics.filter(m => PD.sheets[m.s]).map(m => m.l);
    const radarBrandsRaw = brand !== 'All' ? [brand] : topItems('Weekly+', 5, cat, brand);
    const radarBrands = radarBrandsRaw.slice(0, 5);
    const radarDS = radarBrands.map((b, i) => {
      const vals = radarMetrics.filter(m => PD.sheets[m.s]).map(m => { const r = findRow(m.s, b, 'Brand'); return r && r.values[period] != null ? (r.values[period] * 100) : 0; });
      const c = itemColor(b, 'Brand');
      return { label: shortBrand(b), data: vals, borderColor: c, backgroundColor: c + '30', borderWidth: 2, pointBackgroundColor: c, pointRadius: 3 };
    });
    if (radarLabels.length && radarDS.length) mkRadar('fRadar', radarLabels, radarDS);

    // 3. Line — filtered trend
    const fItems = brand !== 'All' ? [brand] : topItems(ms, 8, cat, brand);
    const per = getPeriods(ms);
    if (fItems.length) mkTrend('fLine', ms, fItems, per);

    // 4. Scatter — Weekly+ (x) vs TOM (y) for all brands
    const sW = PD.sheets['Weekly+'], sT = PD.sheets['TOM'];
    if (sW && sT) {
      const wData = getData('Weekly+'), tData = getData('TOM');
      const allBrands = [...new Set(wData.map(d => d.dims.Brand).filter(b => b && b !== 'Grand Total' && b !== '(blank)'))];
      const tcccPts = [], otherPts = [];
      allBrands.forEach(b => {
        const wr = wData.find(d => d.dims.Brand === b && d.values[period] != null);
        const tr = tData.find(d => d.dims.Brand === b && d.values[period] != null);
        if (!wr || !tr) return;
        const pt = { x: wr.values[period] * 100, y: tr.values[period] * 100, label: shortBrand(b) };
        (TCCC.has(b) ? tcccPts : otherPts).push(pt);
      });
      const ds = [];
      if (tcccPts.length) ds.push({ label: 'TCCC Brands', data: tcccPts, backgroundColor: '#E61A27', pointRadius: 6, pointHoverRadius: 8 });
      if (otherPts.length) ds.push({ label: 'Competitor Brands', data: otherPts, backgroundColor: '#1565C0', pointRadius: 5, pointHoverRadius: 7 });
      if (ds.length) mkScatter('fScatter', ds, 'Weekly+ Incidence', 'TOM');
    }

    // 5. Bubble — Total Awareness (x) vs Affinity (y), size = Weekly+
    const sA = PD.sheets['Total Awareness'], sAf = PD.sheets['Affinity & Cons TB'];
    if (sA && sAf && sW) {
      const aData = getData('Total Awareness'), afData = getData('Affinity & Cons TB'), wData2 = getData('Weekly+');
      const allB2 = [...new Set(aData.map(d => d.dims.Brand).filter(b => b && b !== 'Grand Total' && b !== '(blank)'))];
      const tcccB = [], otherB = [];
      allB2.forEach(b => {
        const ar = aData.find(d => d.dims.Brand === b && d.values[period] != null);
        const afr = afData.find(d => d.dims.Brand === b && d.values[period] != null);
        const wr = wData2.find(d => d.dims.Brand === b && d.values[period] != null);
        if (!ar || !afr) return;
        const sz = wr ? (wr.values[period] * 100) * 3 + 4 : 5;
        const pt = { x: ar.values[period] * 100, y: afr.values[period] * 100, r: Math.max(sz, 3), label: shortBrand(b) };
        (TCCC.has(b) ? tcccB : otherB).push(pt);
      });
      const ds2 = [];
      if (tcccB.length) ds2.push({ label: 'TCCC Brands', data: tcccB, backgroundColor: 'rgba(230,26,39,0.6)', borderColor: '#E61A27', borderWidth: 1 });
      if (otherB.length) ds2.push({ label: 'Competitor Brands', data: otherB, backgroundColor: 'rgba(21,101,192,0.5)', borderColor: '#1565C0', borderWidth: 1 });
      if (ds2.length) mkBubble('fBubble', ds2, 'Total Awareness', 'Affinity TB');
    }
  }

  /* ═══ VIEWS ═══ */
  function renderOverview() {
    const brand = $('#fBrand').value; const period = curPeriod(); const geo = curGeo();
    const ms = ($('#fMetric') || {}).value === 'All' ? 'Weekly+' : $('#fMetric').value;
    const cat = ($('#fCategory') || {}).value || 'All';
    const mL = ($('#fMetric') || {}).value === 'All' ? 'Weekly+ Incidence' : (METRIC_MAP[ms] || ms);
    const dim = getDim(ms); const ti = topItems(ms, 10, cat, brand);
    const isAll = brand === 'All';

    let h = buildKPIs(period);

    h += `<div class="charts-row">
      <div class="chart-card"><h3>${mL} — Trend (${geo})</h3><div class="chart-sub">Top ${dim === 'Brand' ? 'brands' : 'items'}, rolling</div>
        <div class="chart-wrap"><canvas id="trendChart"></canvas></div></div>
      <div class="chart-card"><h3>${dim} Ranking — ${shortP(period)}</h3><div class="chart-sub">${mL}</div>
        <div class="chart-wrap"><canvas id="barChart"></canvas></div></div></div>`;

    h += `<div class="charts-row">
      <div class="chart-card"><h3>Change Analysis</h3><div class="chart-sub">Ch vs LY &amp; LM (pts)</div>
        <div class="chart-wrap"><canvas id="changeChart"></canvas></div></div>
      <div class="chart-card"><h3>Occasion Share</h3><div class="chart-sub">${shortP(period)} — ${geo}</div>
        <div class="chart-wrap"><canvas id="occasionChart"></canvas></div></div></div>`;

    if (hasFilter()) {
      h += buildFilteredSection(ms, period, cat, brand, geo);
    } else {
      h += `<div class="filter-hint">Apply a filter above (Geography, Metric, Category, or Brand) to unlock detailed analysis with Pie, Radar, Scatter, and Bubble charts.</div>`;
    }

    $('#dashContent').innerHTML = h;

    const per = getPeriods(ms);
    const tI = (isAll || dim !== 'Brand') ? ti.slice(0, 10) : [...new Set([brand, ...ti])].slice(0, 10);
    mkTrend('trendChart', ms, tI, per);
    mkBar('barChart', ms, period, 15);
    mkChange('changeChart', ms, period, ti.slice(0, 10));

    const occData = getData('Occasions Shares');
    const occItems = occData.filter(d => d.dims.Occasion && d.dims.Occasion !== 'Grand Total' && d.values[period] !== undefined)
      .map(d => ({ n: d.dims.Occasion, v: d.values[period] })).sort((a, b) => b.v - a.v).slice(0, 12);
    if (occItems.length) mkDonut('occasionChart', occItems.map(i => i.n), occItems.map(i => i.v * 100), PALETTE.slice(0, occItems.length));

    if (hasFilter()) renderFilteredCharts(ms, period, cat, brand);
  }

  function renderTrends() {
    const mRaw = $('#fMetric').value; const ms = mRaw === 'All' ? 'Weekly+' : mRaw;
    const brand = $('#fBrand').value; const isAll = brand === 'All'; const geo = curGeo();
    const dim = getDim(ms); const ti = topItems(ms, 10);
    const tI = (isAll || dim !== 'Brand') ? ti.slice(0, 12) : [...new Set([brand, ...ti])].slice(0, 12);
    const per = getPeriods(ms);
    let h = `<div class="chart-card" style="margin-bottom:20px"><h3>${METRIC_MAP[ms] || ms} — Full Trend (${geo})</h3><div class="chart-sub">All periods</div>
      <div class="chart-wrap" style="height:400px"><canvas id="fullTrend"></canvas></div></div>`;
    const sheets = ['Weekly+', 'Past 7 Day', 'TOM', 'Spont Awareness', 'Total Awareness', 'Affinity & Cons TB'];
    h += '<div class="charts-row">';
    sheets.forEach((sh, i) => { if (!PD.sheets[sh]) return; h += `<div class="chart-card"><h3>${METRIC_MAP[sh] || sh}</h3><div class="chart-sub">${isAll ? 'Top brands' : shortBrand(brand) + ' vs top'} (${geo})</div><div class="chart-wrap" style="height:240px"><canvas id="mt${i}"></canvas></div></div>`; if (i % 2 === 1) h += '</div><div class="charts-row">'; });
    h += '</div>'; $('#dashContent').innerHTML = h;
    mkTrend('fullTrend', ms, tI, per);
    sheets.forEach((sh, i) => { if (!PD.sheets[sh]) return; const t3 = topItems(sh, 5); const br = isAll ? t3 : [...new Set([brand, ...t3])].slice(0, 5); mkTrend('mt' + i, sh, br, getPeriods(sh)); });
  }

  function renderEquity() {
    const brand = $('#fBrand').value; const isAll = brand === 'All'; const period = curPeriod(); const geo = curGeo();
    const eqSh = ['Affinity & Cons TB', 'Affinity & Cons T2B', 'Total Awareness', 'Spont Awareness', 'TOM', 'Brand Power '];
    let h = '<div class="kpi-row">';
    eqSh.forEach(sh => { const row = findRow(sh, brand, 'Brand'); let val = '—', ch = null; if (row) { const v = row.values[period]; val = v !== undefined ? (isPct(sh) ? fP(v) : v.toFixed(2)) : '—'; ch = row.chVsLY; } const dir = chDir(ch);
      h += `<div class="kpi-card"><div class="kpi-label">${METRIC_MAP[sh] || sh}</div><div style="font-size:24px;font-weight:900;margin:6px 0">${val}</div>${ch != null ? `<span class="kpi-change ${dir}">${chArr(ch)} ${isPct(sh) ? fPP(ch) : ch.toFixed(2)}</span>` : ''}</div>`; });
    h += '</div>';
    h += `<div class="charts-row"><div class="chart-card"><h3>Affinity TB — ${geo}</h3><div class="chart-sub">${shortP(period)}</div><div class="chart-wrap" style="height:350px"><canvas id="eqBar1"></canvas></div></div>
      <div class="chart-card"><h3>Total Awareness — ${geo}</h3><div class="chart-sub">${shortP(period)}</div><div class="chart-wrap" style="height:350px"><canvas id="eqBar2"></canvas></div></div></div>`;
    $('#dashContent').innerHTML = h; mkBar('eqBar1', 'Affinity & Cons TB', period, 15); mkBar('eqBar2', 'Total Awareness', period, 15);
  }

  function renderOccasions() {
    const period = curPeriod(); const geo = curGeo();
    let h = `<div class="charts-row"><div class="chart-card"><h3>Occasion Share (${geo})</h3><div class="chart-sub">${shortP(period)}</div><div class="chart-wrap" style="height:360px"><canvas id="occ1"></canvas></div></div>
      <div class="chart-card"><h3>At Home vs Away (${geo})</h3><div class="chart-sub">${shortP(period)}</div><div class="chart-wrap" style="height:360px"><canvas id="ahafh"></canvas></div></div></div>`;
    const occS = PD.sheets['Occasions Shares'];
    if (occS) h += `<div class="chart-card" style="margin-bottom:20px"><h3>Occasion Trends (${geo})</h3><div class="chart-sub">Top occasions</div><div class="chart-wrap" style="height:340px"><canvas id="occTrend"></canvas></div></div>`;
    $('#dashContent').innerHTML = h;

    const occData = getData('Occasions Shares');
    const occItems = occData.filter(d => d.dims.Occasion && d.dims.Occasion !== 'Grand Total' && d.values[period] !== undefined).map(d => ({ n: d.dims.Occasion, v: d.values[period] })).sort((a, b) => b.v - a.v);
    if (occItems.length) mkDonut('occ1', occItems.map(i => i.n), occItems.map(i => i.v * 100), PALETTE.slice(0, occItems.length));

    const ahS = PD.sheets['AH_AFH'];
    if (ahS) { const ahD = getData('AH_AFH'); const brands = [...new Set(ahD.map(d => d.dims.Brand))].filter(b => b && b !== 'Grand Total' && b !== '(blank)').slice(0, 8); const ah = [], afh = [];
      brands.forEach(b => { const r1 = ahD.find(d => d.dims.Brand === b && (d.dims['Where (AH/AFH)'] || '').includes('At Home')); const r2 = ahD.find(d => d.dims.Brand === b && (d.dims['Where (AH/AFH)'] || '').includes('Away')); ah.push(r1 ? r1.values[period] * 100 : 0); afh.push(r2 ? r2.values[period] * 100 : 0); });
      killChart('ahafh'); const ctx = document.getElementById('ahafh'); if (ctx) charts['ahafh'] = new Chart(ctx, { type: 'bar', data: { labels: brands.map(shortBrand), datasets: [{ label: 'At Home', data: ah, backgroundColor: '#E61A27', borderRadius: 3, barPercentage: .6 }, { label: 'Away From Home', data: afh, backgroundColor: '#1565C0', borderRadius: 3, barPercentage: .6 }] }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } } }, scales: { x: { stacked: true, grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 45 } }, y: { stacked: true, grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(0) + '%' } } } } }); }

    if (occS) { const per = getPeriods('Occasions Shares'); const top6 = occData.filter(d => d.dims.Occasion && d.dims.Occasion !== 'Grand Total').sort((a, b) => (b.values[period] || 0) - (a.values[period] || 0)).slice(0, 6);
      killChart('occTrend'); const ctx = document.getElementById('occTrend');
      if (ctx && top6.length) charts['occTrend'] = new Chart(ctx, { type: 'line', data: { labels: per.map(shortP), datasets: top6.map((d, i) => ({ label: d.dims.Occasion, data: per.map(p => (d.values[p] || 0) * 100), borderColor: PALETTE[i], borderWidth: 2, pointRadius: 2, tension: .3, fill: false })) }, options: { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 45 } }, y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 }, callback: v => v.toFixed(1) + '%' } } } } }); }
  }

  function mkTable(sh, period, cat, brandFilter) {
    const s = PD.sheets[sh || 'Weekly+']; if (!s) return '<div class="table-section"><div class="table-header"><h3>No data</h3></div></div>';
    const pct = isPct(sh); const dim = getDim(sh); let data = getData(sh);
    let items = data.filter(d => d.dims[dim] && d.dims[dim] !== 'Grand Total' && d.dims[dim] !== '(blank)');
    if (cat && cat !== 'All' && s.hasBrand) items = items.filter(d => (d.dims.Category || d.dims['Drink Type'] || '') === cat);
    if (brandFilter && brandFilter !== 'All' && s.hasBrand) items = items.filter(d => d.dims.Brand === brandFilter);
    items.sort((a, b) => (b.values[period] || 0) - (a.values[period] || 0));
    const pers = getPeriods(sh).slice(-5);
    const activeDims = (curGeo() !== 'All India' && s.stateDims) ? s.stateDims : s.dimNames;
    const extraDims = activeDims.filter(n => n !== dim && n !== 'L2_State');
    let h = `<div class="table-section"><div class="table-header"><h3>${METRIC_MAP[sh] || sh} — ${curGeo()}</h3><span class="count">${items.length} rows</span></div><div class="table-wrap"><table><thead><tr><th>${dim}</th>`;
    extraDims.forEach(c => { h += `<th>${c}</th>`; }); pers.forEach(p => { h += `<th>${shortP(p)}</th>`; }); h += '<th>Ch vs LY</th><th>Ch vs LM</th></tr></thead><tbody>';
    items.slice(0, 100).forEach(d => { const b = d.dims[dim] || ''; const isTCCC = dim === 'Brand' && TCCC.has(b); const c = itemColor(b, dim);
      h += `<tr><td><div class="brand-cell"><span class="brand-dot" style="background:${c}"></span><span class="${isTCCC ? 'tccc' : ''}">${dim === 'Brand' ? shortBrand(b) : b}</span></div></td>`;
      extraDims.forEach(c2 => { h += `<td>${d.dims[c2] || ''}</td>`; }); pers.forEach(p => { const v = d.values[p]; h += `<td>${v !== undefined ? fV(v, pct) : '—'}</td>`; });
      h += `<td class="${d.chVsLY > 0 ? 'val-up' : 'val-down'}">${pct ? fPP(d.chVsLY) : fN(d.chVsLY)}</td><td class="${d.chVsLM > 0 ? 'val-up' : 'val-down'}">${pct ? fPP(d.chVsLM) : fN(d.chVsLM)}</td></tr>`; });
    return h + '</tbody></table></div></div>';
  }

  function renderTable() {
    const mRaw = $('#fMetric').value; const sh = mRaw === 'All' ? 'Weekly+' : mRaw; const period = curPeriod(); const cat = $('#fCategory').value; const brand = $('#fBrand').value;
    let h = '<div class="sheet-nav">'; Object.keys(PD.sheets).forEach(s => { h += `<button class="sheet-btn ${s === sh ? 'active' : ''}" data-sheet="${s}">${s.trim()}</button>`; }); h += '</div>';
    h += mkTable(sh, period, cat, brand); $('#dashContent').innerHTML = h;
    $$('.sheet-btn').forEach(btn => { btn.onclick = () => { $$('.sheet-btn').forEach(b => b.classList.remove('active')); btn.classList.add('active'); const t = $('.table-section'); if (t) t.outerHTML = mkTable(btn.dataset.sheet, period, cat, brand); }; });
  }

  /* ═══ NAVIGATION ═══ */
  let currentTab = 'overview';
  function switchTab(tab) { currentTab = tab; $$('.tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tab)); ({ overview: renderOverview, trends: renderTrends, equity: renderEquity, occasions: renderOccasions, table: renderTable })[tab](); }

  function populateFilters() {
    const geoSel = $('#fGeo'); geoSel.innerHTML = '';
    let o = document.createElement('option'); o.value = 'All India'; o.textContent = 'All India'; geoSel.appendChild(o);
    [...PD.states].sort().forEach(s => { o = document.createElement('option'); o.value = s; o.textContent = s; geoSel.appendChild(o); });
    const metricSel = $('#fMetric'); metricSel.innerHTML = '';
    o = document.createElement('option'); o.value = 'All'; o.textContent = 'All Metrics'; metricSel.appendChild(o);
    Object.keys(PD.sheets).forEach(sh => { o = document.createElement('option'); o.value = sh; o.textContent = METRIC_MAP[sh] || sh.trim(); metricSel.appendChild(o); });
    const catSel = $('#fCategory'); catSel.innerHTML = '<option value="All">All Categories</option>';
    [...PD.categories].sort().forEach(c => { o = document.createElement('option'); o.value = c; o.textContent = c; catSel.appendChild(o); });
    const brandSel = $('#fBrand'); brandSel.innerHTML = '';
    o = document.createElement('option'); o.value = 'All'; o.textContent = 'All Brands'; brandSel.appendChild(o);
    [...PD.brands].sort((a, b) => shortBrand(a).localeCompare(shortBrand(b))).forEach(b => { o = document.createElement('option'); o.value = b; o.textContent = shortBrand(b); brandSel.appendChild(o); });
    $('#periodBadge').innerHTML = '&#x1F4C5; ' + shortP(PD.periods[PD.periods.length - 1] || '');
  }

  function handleFile(file) {
    if (!file) return; $('#upload-progress').style.display = 'block'; $('#upload-status').textContent = 'Reading workbook...'; $('#progressFill').style.width = '20%'; $('#fileName').textContent = file.name;
    const reader = new FileReader(); reader.onload = function (e) { try {
      $('#upload-status').textContent = 'Parsing sheets...'; $('#progressFill').style.width = '50%';
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
      $('#upload-status').textContent = `Found ${wb.SheetNames.length} sheets...`; $('#progressFill').style.width = '80%';
      PD = parseWorkbook(wb); $('#progressFill').style.width = '100%';
      $('#upload-status').textContent = `Done! ${Object.keys(PD.sheets).length} sheets, ${PD.brands.size} brands, ${PD.states.size} states.`;
      setTimeout(() => { $('#upload-screen').style.display = 'none'; $('#dashboard').style.display = 'block'; populateFilters(); switchTab('overview'); }, 500);
    } catch (err) { $('#upload-status').textContent = 'Error: ' + err.message; console.error(err); } }; reader.readAsArrayBuffer(file);
  }

  function init() {
    const dz = $('#dropZone'), fi = $('#fileInput');
    dz.onclick = () => fi.click(); fi.onchange = e => { if (e.target.files[0]) handleFile(e.target.files[0]); };
    dz.ondragover = e => { e.preventDefault(); dz.classList.add('dragover'); }; dz.ondragleave = () => dz.classList.remove('dragover');
    dz.ondrop = e => { e.preventDefault(); dz.classList.remove('dragover'); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); };
    $$('.tab').forEach(t => { t.onclick = () => switchTab(t.dataset.tab); });
    ['#fGeo', '#fMetric', '#fCategory', '#fBrand'].forEach(s => { const el = $(s); if (el) el.onchange = () => switchTab(currentTab); });
    $('#btnUploadNew').onclick = () => { $('#dashboard').style.display = 'none'; $('#upload-screen').style.display = 'flex'; $('#upload-progress').style.display = 'none'; };
  }

  init();
}

export default function BeachDashboard() {
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return; // guard against React StrictMode double-invoke
    bootstrapped.current = true;

    // Inject the original stylesheet once.
    if (!document.getElementById("beach-dashboard-styles")) {
      const style = document.createElement("style");
      style.id = "beach-dashboard-styles";
      style.textContent = CSS;
      document.head.appendChild(style);
    }

    // Inter font (the original linked it in <head>).
    if (!document.getElementById("beach-dashboard-font")) {
      const link = document.createElement("link");
      link.id = "beach-dashboard-font";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }

    Promise.all([
      loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"),
      loadScript("https://cdn.jsdelivr.net/npm/chart.js@4.4.4/dist/chart.umd.min.js"),
    ])
      .then(() => runApp())
      .catch((err) => console.error("BeachDashboard: failed to load dependencies", err));
  }, []);

  return (
    <>
      <div id="upload-screen">
        <img className="upload-logo" src="kognition-logo.jpg" alt="Coca-Cola" />
        <div className="upload-title">BEACH Analytics Dashboard</div>
        <div className="upload-sub">India &amp; Southwest Asia — Consumer Measurement</div>
        <div className="drop-zone" id="dropZone">
          <div className="icon">📊</div>
          <h3>Drop your BEACH Excel file here</h3>
          <p>or click to browse — supports .xlsx format</p>
          <input type="file" id="fileInput" accept=".xlsx,.xls" />
        </div>
        <div id="upload-progress">
          <div className="progress-bar"><div className="progress-fill" id="progressFill"></div></div>
          <div id="upload-status">Reading workbook...</div>
        </div>
        <div className="upload-hint">Upload any BEACH Playground Excel file to generate a comprehensive analytics view.</div>
      </div>

      <div id="dashboard">
        <div className="dash-header">
          <img className="logo" src="1.png" alt="Coca-Cola" />
          <div className="divider"></div>
          <div className="title">KOGNOS AI - BEACH <span>Analytics</span></div>
          <div className="spacer"></div>
          <div className="period-badge" id="periodBadge">📅 —</div>
          <div className="file-name" id="fileName">—</div>
          <button className="btn" id="btnUploadNew">↻ New File</button>
        </div>

        <div className="tab-bar" id="tabBar">
          <div className="tab active" data-tab="overview">Overview</div>
          <div className="tab" data-tab="trends">Trends</div>
          <div className="tab" data-tab="equity">Equity</div>
          <div className="tab" data-tab="occasions">Occasions</div>
          <div className="tab" data-tab="table">Data Table</div>
        </div>

        <div className="filter-bar" id="filterBar">
          <div className="filter-group"><label>Geography</label><select id="fGeo"></select></div>
          <div className="filter-group"><label>Metric</label><select id="fMetric"></select></div>
          <div className="filter-group"><label>Category</label><select id="fCategory"><option value="All">All Categories</option></select></div>
          <div className="filter-group"><label>Focus Brand</label><select id="fBrand"></select></div>
        </div>

        <div className="dash-content" id="dashContent"></div>
      </div>
    </>
  );
}